/**
 * BillingSettings
 *
 * Displays subscription summary and a downloadable invoice list.
 * Data comes from /api/billing/overview and /api/billing/invoices.
 *
 * Design rules followed:
 * - Matches the existing Settings card visual language (rounded-2xl, gradient top strip)
 * - useTimezone() for all date formatting — never toLocaleDateString()
 * - No focus rings (project rule) — subtle border-color shift on focus only
 * - No `any` types
 * - React Query for server state (staleTime 60 s)
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTimezone } from '@/contexts/TimezoneContext';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { billingService, BillingInvoice, BillingScope, BillingSubscription } from '@/services/billing.service';
import { EarlySupporterBillingSection } from '@/components/earlySupporter/EarlySupporterBillingSection';
import { useEsCheckout } from '@/hooks/useEarlySupporter';
import { useShopOrderStatus } from '@/hooks/useShopOrderStatus';
import { useAuth } from '@/hooks/useAuth';
import { useShopCartStore } from '@/stores/shopCartStore';
import { isSafeShopUrl } from '@/services/shop.api';
import { formatEsMoney } from '@/utils/earlySupporterFormat';

// ---------------------------------------------------------------------------
// Status badge colour helper
// ---------------------------------------------------------------------------

function statusBadgeClass(status: string): string {
  switch (status.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-700 border border-green-200';
    case 'pending':
    case 'pending-cancel':
    case 'on-hold':
      return 'bg-amber-100 text-amber-700 border border-amber-200';
    case 'cancelled':
    case 'expired':
      return 'bg-red-100 text-red-700 border border-red-200';
    case 'completed':
      return 'bg-primary-100 text-primary-700 border border-primary-200';
    default:
      return 'bg-gray-100 text-gray-600 border border-gray-200';
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

// ---------------------------------------------------------------------------
// Skeleton helpers
// ---------------------------------------------------------------------------

const SkeletonLine = ({ className }: { className?: string }) => (
  <div className={cn('animate-pulse bg-gray-100 rounded', className)} />
);

// ---------------------------------------------------------------------------
// Invoice row
// ---------------------------------------------------------------------------

interface InvoiceRowProps {
  invoice: BillingInvoice;
  scope: BillingScope;
}

const InvoiceRow = ({ invoice, scope }: InvoiceRowProps) => {
  const { formatDateOnly } = useTimezone();
  const { t, i18n } = useTranslation('settings');
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await billingService.downloadInvoicePdf(
        invoice.orderId,
        invoice.invoiceNumber ?? `order-${invoice.number}`,
        scope,
      );
    } catch {
      setDownloadError(t('billing_download_error'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <tr className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-3 border-t border-gray-100 px-4 py-4 transition-colors hover:bg-gray-50/60 sm:table-row sm:px-0 sm:py-0">
      {/* Date */}
      <td className="order-3 min-w-0 py-0 text-sm text-gray-700 sm:table-cell sm:px-4 sm:py-3 sm:whitespace-nowrap">
        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500 sm:hidden">{t('billing_col_date')}</span>
        {invoice.date ? formatDateOnly(invoice.date) : '—'}
      </td>
      {/* Number: "Invoice 208" (+ muted order #), or a labelled "Order #14079" until an invoice exists */}
      <td className="order-1 min-w-0 py-0 text-sm font-mono text-gray-600 sm:table-cell sm:px-4 sm:py-3 sm:whitespace-nowrap">
        {invoice.invoiceNumber ? (
          <div className="flex flex-col">
            <span>{t('billing_invoice_number', { number: invoice.invoiceNumber })}</span>
            <span className="text-xs text-gray-400">
              {t('billing_order_number', { number: invoice.number })}
            </span>
          </div>
        ) : (
          <span className="text-gray-500">{t('billing_order_number', { number: invoice.number })}</span>
        )}
      </td>
      {/* Amount */}
      <td className="order-4 min-w-0 py-0 text-right text-sm font-medium text-gray-900 sm:table-cell sm:px-4 sm:py-3 sm:text-left sm:whitespace-nowrap">
        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500 sm:hidden">{t('billing_col_amount')}</span>
        {formatEsMoney(invoice.amount, invoice.currency, locale)}
      </td>
      {/* Status */}
      <td className="order-2 justify-self-end py-0 sm:table-cell sm:px-4 sm:py-3">
        <span className={cn('inline-flex px-2.5 py-1 rounded-full text-xs font-medium', statusBadgeClass(invoice.status))}>
          {capitalize(invoice.status)}
        </span>
      </td>
      {/* Download */}
      <td className="order-5 col-span-2 py-0 text-right sm:table-cell sm:px-4 sm:py-3">
        {invoice.hasInvoice ? (
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className={cn(
                'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-medium transition-colors sm:min-h-0 sm:px-3',
                downloading
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-900 text-white hover:bg-gray-700'
              )}
              aria-label={
                invoice.invoiceNumber
                  ? t('billing_download_aria', { number: invoice.invoiceNumber })
                  : t('billing_download_aria_order', { number: invoice.number })
              }
            >
              {downloading ? (
                <>
                  <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" aria-hidden="true" />
                  {t('billing_downloading')}
                </>
              ) : (
                <>
                  <Icon name={ICONS.DOWNLOAD} size="xs" ariaHidden />
                  {t('billing_download')}
                </>
              )}
            </button>
            {downloadError && (
              <span className="text-xs text-red-600">{downloadError}</span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400">{t('billing_no_invoice_file')}</span>
        )}
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface BillingSettingsProps {
  scope?: BillingScope;
}

export const BillingSettings = ({ scope = 'team' }: BillingSettingsProps) => {
  const { t, i18n } = useTranslation('settings');
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const { formatDateOnly } = useTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const checkoutId = searchParams.get('checkout');
  const shopPurchase = searchParams.get('purchase') === 'success' && !!(checkoutId || searchParams.get('order'));
  const checkoutStatus = useEsCheckout(shopPurchase ? checkoutId : null);
  const rawOrder = Number.parseInt(searchParams.get('order') ?? '', 10);
  const orderId = Number.isInteger(rawOrder) && rawOrder > 0 ? rawOrder : null;
  const addonOnly = shopPurchase && !checkoutId && orderId !== null;
  const orderStatus = useShopOrderStatus(addonOnly ? orderId : null);
  const clearedCart = useRef(false);
  const billingRefreshStarted = useRef(false);
  const [billingRefreshComplete, setBillingRefreshComplete] = useState(false);
  const clearCart = useShopCartStore((state) => state.clear);
  const { user, refreshUser } = useAuth();

  const activated = checkoutId
    ? checkoutStatus.data?.status === 'PAID' && checkoutStatus.data.activated
    : orderStatus.state === 'activated' || orderStatus.state === 'completed_unlinked';
  const needsReview = !checkoutId && orderStatus.state === 'needs_review';
  const timedOut = checkoutId ? checkoutStatus.timedOut : orderStatus.timedOut;
  const purchaseFailed = checkoutStatus.data?.status === 'FAILED' || checkoutStatus.data?.status === 'VOIDED';

  useEffect(() => {
    if (!shopPurchase || !(activated || needsReview)) return;
    if (!clearedCart.current) {
      clearedCart.current = true;
      clearCart();
      queryClient.invalidateQueries({ queryKey: ['early-supporter'] });
      // Add-ons AND Plan activations: token balance / package usage / team + Brand Portfolio data,
      // plus auth/me (agency staff status, whitelabel flag, plan limits). Settings.tsx redirects to the
      // Brand Portfolio billing once auth/me shows this team is portfolio-managed.
      queryClient.invalidateQueries({ queryKey: ['team'] });
      queryClient.invalidateQueries({ queryKey: ['agency'] });
      void refreshUser().catch(() => undefined);
    }
    if (activated && !billingRefreshStarted.current) {
      billingRefreshStarted.current = true;
      void Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['billing-overview'] }),
        queryClient.invalidateQueries({ queryKey: ['billing-invoices'] }),
      ]).then(() => setBillingRefreshComplete(true));
    }
  }, [shopPurchase, activated, needsReview, checkoutId, scope, clearCart, queryClient, refreshUser]);

  const dismissPurchase = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('purchase');
    next.delete('order');
    next.delete('checkout');
    setSearchParams(next, { replace: true });
  };

  const {
    data: overview,
    isLoading: overviewLoading,
    isFetching: overviewFetching,
    isError: overviewError,
  } = useQuery({
    queryKey: ['billing-overview', scope],
    queryFn: () => billingService.getOverview(scope),
    staleTime: 60_000,
    retry: 1,
  });

  const {
    data: invoices,
    isLoading: invoicesLoading,
    isFetching: invoicesFetching,
    isError: invoicesError,
  } = useQuery({
    queryKey: ['billing-invoices', scope],
    queryFn: () => billingService.getInvoices(scope),
    staleTime: 60_000,
    retry: 1,
  });

  const isAgencyAdminStaff = !!user?.isAgencyStaff && (user.agencyRole === 'OWNER' || user.agencyRole === 'ADMIN');
  const portfolioManaged = !overview?.hasBilling && overview?.reason === 'agency_managed';
  const purchasePending = shopPurchase && !activated && !purchaseFailed && !needsReview && !timedOut;
  const purchaseBillingRefreshing = shopPurchase && (purchasePending || (activated && (!billingRefreshComplete || overviewFetching || invoicesFetching)));
  const addonSubscriptions = overview?.hasBilling
    ? [
        ...(overview.subscription?.kind === 'addon' ? [overview.subscription] : []),
        ...(overview.addonSubscriptions ?? []),
      ].filter((addon, index, all) => all.findIndex((entry) => entry.id === addon.id) === index)
    : [];
  const hasOnlyAddonSubscription = overview?.hasBilling && addonSubscriptions.length > 0 && overview.subscription?.kind !== 'plan';

  const billingCycle = (subscription: BillingSubscription) => {
    const period = subscription.billingPeriod.toLowerCase();
    const localized = t(`billing_period_${period}`, { count: subscription.billingInterval, defaultValue: subscription.billingPeriod });
    return subscription.billingInterval === 1 ? localized : `${subscription.billingInterval} ${localized}`;
  };

  const billingStatus = (status: string) => t(`billing_status_${status.toLowerCase().replace(/[^a-z]+/g, '_')}`, { defaultValue: capitalize(status) });

  // ---------------------------------------------------------------------------
  // Subscription summary card
  // ---------------------------------------------------------------------------

  const renderSubscription = () => {
    if (overviewLoading || purchaseBillingRefreshing) {
      return (
        <div className="space-y-3 p-5 sm:p-6">
          <SkeletonLine className="h-5 w-1/3" />
          <SkeletonLine className="h-4 w-1/2" />
          <SkeletonLine className="h-4 w-2/5" />
          <SkeletonLine className="h-4 w-1/3" />
        </div>
      );
    }

    if (overviewError) {
      return (
        <div className="p-5 sm:p-6">
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Icon name={ICONS.CAUTION} size="sm" color="text-amber-500" ariaHidden />
            {t('billing_load_error')}
          </p>
        </div>
      );
    }

    if ((!overview || !overview.hasBilling) && purchasePending) {
      // Activating: the "no billing info" empty state would read as an error during the wait.
      return (
        <div className="space-y-3 p-5 sm:p-6" aria-busy="true">
          <SkeletonLine className="h-5 w-1/3" />
          <SkeletonLine className="h-4 w-1/2" />
          <SkeletonLine className="h-4 w-2/5" />
        </div>
      );
    }

    if (!overview || !overview.hasBilling) {
      const isAgencyManaged = portfolioManaged;
      return (
        <div className="p-5 sm:p-6">
          <p className="text-sm text-gray-500">
            {isAgencyManaged
              ? t('billing_agency_managed', { defaultValue: 'Billing for this brand is managed by your Brand Portfolio.' })
              : t(scope === 'agency' ? 'billing_no_billing_info_portfolio' : 'billing_no_billing_info')}
          </p>
          {isAgencyManaged && isAgencyAdminStaff && (
            <Link to="/agency/settings?tab=billing" className="mt-2 inline-flex min-h-[44px] items-center rounded-md border border-transparent text-sm font-semibold text-primary-700 focus-visible:border-primary-700">
              {t('billing_agency_managed_link')}
            </Link>
          )}
        </div>
      );
    }

    const { subscription, customer } = overview;

    if (!subscription || subscription.kind === 'addon') {
      return (
        <div className="p-5 sm:p-6 space-y-1">
          <p className="text-sm text-gray-700">
            {t('billing_customer_email_label')}{' '}
            <span className="font-medium">{customer.email}</span>
          </p>
          <p className="text-sm text-gray-500">{subscription?.kind === 'addon' || addonSubscriptions.length > 0 ? t('billing_addons_only_body') : t('billing_no_active_subscription')}</p>
        </div>
      );
    }

    // Format: "19.00€/month" or "190.00€/year"
    const periodLabel = billingCycle(subscription);

    return (
      <div className="p-5 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Amount + period */}
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">
              {t('billing_subscription_amount')}
            </p>
            <p className="whitespace-nowrap text-2xl font-bold tabular-nums text-gray-900">
              {formatEsMoney(subscription.amount, subscription.currency, locale)}
              <span className="text-sm font-normal text-gray-500">
                /{periodLabel}
              </span>
            </p>
          </div>

          {/* Status */}
          <div className="flex sm:justify-end items-start">
            <span className={cn('inline-flex px-3 py-1.5 rounded-full text-xs font-semibold', statusBadgeClass(subscription.status))}>
              {billingStatus(subscription.status)}
            </span>
          </div>

          {/* Plan name(s) */}
          {subscription.items.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">
                {t('billing_plan_items')}
              </p>
              <ul className="space-y-0.5">
                {subscription.items.map((item, idx) => (
                  <li key={idx} className="text-sm text-gray-700 font-medium">
                    {item.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Dates */}
          <div className="space-y-2">
            {subscription.nextRenewal && (
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-400 mb-0.5">
                  {t('billing_next_renewal')}
                </p>
                <p className="text-sm text-gray-700">{formatDateOnly(subscription.nextRenewal)}</p>
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-0.5">
                {t('billing_started')}
              </p>
              <p className="text-sm text-gray-700">
                {subscription.startDate ? formatDateOnly(subscription.startDate) : '—'}
              </p>
            </div>
          </div>

          {/* Customer email */}
          <div className="sm:col-span-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              {t('billing_customer_email_label')}{' '}
              <span className="text-gray-700">{customer.email}</span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderAddons = () => {
    if (!addonSubscriptions.length) return null;
    return (
      <section className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm" aria-labelledby="billing-addons-title">
        <div className="h-1 bg-gradient-to-r from-primary-400 to-primary-600" />
        <h3 id="billing-addons-title" className="flex items-center gap-2 px-5 pt-5 text-sm font-bold uppercase tracking-wide text-gray-900 sm:px-6">
          <Icon name={ICONS.PLUS} size="sm" color="text-primary-600" ariaHidden />
          {t('billing_addons_title')}
        </h3>
        <div className="divide-y divide-gray-100 px-5 pb-2 pt-3 sm:px-6">
          {addonSubscriptions.map((addon) => (
            <div key={addon.id} className="py-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="min-w-0 text-sm font-semibold text-gray-900">{addon.items[0]?.name || t('billing_addons_title')}</p>
                <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-medium', statusBadgeClass(addon.status))}>{billingStatus(addon.status)}</span>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t('billing_subscription_amount')}</p>
                  <p className="whitespace-nowrap text-sm font-semibold tabular-nums text-gray-900">{formatEsMoney(addon.amount, addon.currency, locale)}<span className="font-normal text-gray-600">/{billingCycle(addon)}</span></p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t('billing_next_renewal')}</p>
                  <p className="text-sm text-gray-700">{addon.nextRenewal ? formatDateOnly(addon.nextRenewal) : t('billing_not_scheduled')}</p>
                </div>
              </div>
              {addon.viewUrl && isSafeShopUrl(addon.viewUrl) && (
                <a href={addon.viewUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex min-h-[44px] items-center gap-1 rounded-md border border-transparent text-sm font-semibold text-primary-700 focus-visible:border-primary-700 sm:min-h-0">
                  {t('billing_addon_manage')} <Icon name={ICONS.EXTERNAL_LINK} size="xs" ariaHidden />
                </a>
              )}
            </div>
          ))}
        </div>
      </section>
    );
  };

  // ---------------------------------------------------------------------------
  // Invoices table
  // ---------------------------------------------------------------------------

  const renderInvoices = () => {
    if (invoicesLoading || purchaseBillingRefreshing) {
      return (
        <div className="p-5 sm:p-6 space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-4">
              <SkeletonLine className="h-4 flex-1" />
              <SkeletonLine className="h-4 w-24" />
              <SkeletonLine className="h-4 w-16" />
              <SkeletonLine className="h-4 w-20" />
            </div>
          ))}
        </div>
      );
    }

    if (invoicesError) {
      return (
        <div className="p-5 sm:p-6">
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Icon name={ICONS.CAUTION} size="sm" color="text-amber-500" ariaHidden />
            {t('billing_load_error')}
          </p>
        </div>
      );
    }

    if (!invoices || invoices.length === 0) {
      return (
        <div className="p-5 sm:p-6">
          <p className="text-sm text-gray-500">{t('billing_no_invoices')}</p>
        </div>
      );
    }

    return (
      <div className="sm:overflow-x-auto">
        <table className="block w-full text-left sm:table" aria-label={t('billing_invoices_table_aria')}>
          <thead className="hidden sm:table-header-group">
            <tr className="bg-gray-50">
              <th className="py-2.5 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">{t('billing_col_date')}</th>
              <th className="py-2.5 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">{t('billing_col_number')}</th>
              <th className="py-2.5 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">{t('billing_col_amount')}</th>
              <th className="py-2.5 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">{t('billing_col_status')}</th>
              <th className="py-2.5 px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">{t('billing_col_invoice')}</th>
            </tr>
          </thead>
          <tbody className="block sm:table-row-group">
            {invoices.map((invoice) => (
              <InvoiceRow key={invoice.orderId} invoice={invoice} scope={scope} />
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="max-w-[1420px] space-y-6 pb-[calc(var(--bottom-nav-h)+1.5rem)] lg:pb-0">
      {shopPurchase && (
        <div role="status" className={`flex w-full flex-wrap items-start gap-3 rounded-xl border p-4 ${activated ? 'border-primary-200 bg-primary-50' : purchaseFailed ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
          <Icon name={activated ? ICONS.CHECK_CIRCLE : purchaseFailed ? ICONS.WARNING : ICONS.CLOCK} size="md" color={activated ? 'text-primary-700' : purchaseFailed ? 'text-red-700' : 'text-amber-800'} ariaHidden />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900">{activated ? t(checkoutId ? 'shop_purchase_success_title' : 'shop_addon_success_title') : purchaseFailed ? t('shop_purchase_failed_title') : needsReview ? t('shop_purchase_review_title') : t('shop_purchase_pending_title')}</p>
            <p className="mt-1 text-sm text-gray-700">{activated ? t(checkoutId ? 'shop_purchase_success_body' : 'shop_addon_success_body') : purchaseFailed ? t('shop_purchase_failed_body') : needsReview ? t('shop_purchase_review_body') : timedOut ? t('shop_purchase_delayed_body') : t('shop_purchase_pending_body')}</p>
          </div>
          {!checkoutId && timedOut && !activated && !needsReview && <button type="button" onClick={orderStatus.restart} className="min-h-[44px] rounded-md border border-transparent px-2 text-sm font-semibold text-primary-700 focus-visible:border-primary-700">{t('shop_check_again')}</button>}
          {(activated || purchaseFailed || needsReview || timedOut) && <button type="button" onClick={dismissPurchase} className="min-h-[44px] rounded-md border border-transparent px-2 text-sm font-semibold text-gray-700 focus-visible:border-gray-700">{t('shop_dismiss')}</button>}
        </div>
      )}
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <div className="flex w-full max-w-3xl min-w-0 flex-col gap-6 xl:max-w-none">
          {/* Active paid Plan management (renders nothing when not applicable) */}
          <EarlySupporterBillingSection />

          {/* Plan summary */}
          <div className="relative min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary-400 to-primary-600" />
            <div className="p-5 pb-0 sm:p-6">
              <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-900">
                <Icon name={ICONS.CREDIT_CARD} size="sm" color="text-primary-600" ariaHidden />
                {t(hasOnlyAddonSubscription ? 'billing_addons_only_title' : 'billing_subscription_title')}
              </h3>
            </div>
            {renderSubscription()}
          </div>

          {renderAddons()}
        </div>

        {/* Invoices */}
        <div className="relative w-full max-w-3xl min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm xl:max-w-none">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-gray-300 to-gray-400" />
          <div className="p-5 sm:p-6 pb-3">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-900">
              <Icon name={ICONS.FILE} size="sm" color="text-gray-500" ariaHidden />
              {t('billing_invoices_title')}
            </h3>
          </div>
          {renderInvoices()}
        </div>
      </div>
    </div>
  );
};
